import { memo, useMemo } from 'react';

import { alpha, Box, Stack, Typography } from '@mui/material';

const bucketCategories = ['Low', 'Medium', 'High', 'Very High'];

const MapLegend = ({
  dataBuckets,
  bucketsCount,
  bucketsColors,
  maxValuePerMap,
  areasNotAvailableCount,
  valueDecimalPlaces = 0,
}: {
  dataBuckets: number[] | null;
  bucketsCount: number[] | null;
  bucketsColors: string[];
  maxValuePerMap: number | null;
  areasNotAvailableCount: number;
  valueDecimalPlaces?: number;
}) => {
  const formattedBuckets = useMemo(() => {
    if (!dataBuckets?.length) {
      return null;
    }

    return dataBuckets.map((bucket) =>
      new Intl.NumberFormat('en-US', {
        maximumFractionDigits: valueDecimalPlaces,
      }).format(bucket)
    );
  }, [dataBuckets, valueDecimalPlaces]);

  const formattedMaxValue = useMemo(() => {
    if (maxValuePerMap === null) {
      return null;
    }

    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: valueDecimalPlaces,
    }).format(maxValuePerMap);
  }, [maxValuePerMap, valueDecimalPlaces]);

  if (
    !formattedBuckets?.length ||
    !formattedMaxValue ||
    !dataBuckets?.length ||
    !bucketsCount?.length
  ) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.7),
        backdropFilter: 'blur(8px)',
        boxShadow: (theme) => theme.customShadows.xs,
        bottom: (theme) => theme.spacing(5),
        right: (theme) => theme.spacing(1.5),
        borderRadius: 0.5,
        p: 1.5,
      }}
    >
      {areasNotAvailableCount ? (
        <Stack direction='row' alignItems='center' gap={1}>
          <Box
            sx={{
              width: 18,
              height: 18,
              bgcolor: 'grey.300',
            }}
          />

          <Typography variant='subtitleLabel' width={58}>
            N/A
          </Typography>

          <Typography variant='subtitleLabel'>
            (N = {areasNotAvailableCount})
          </Typography>
        </Stack>
      ) : null}

      {formattedBuckets.map((bucketValue, index, arr) => {
        const isLastBucket = index === arr.length - 1;
        const nextValue = arr[index + 1] ?? formattedMaxValue;
        const isDuplicate = bucketValue === nextValue;

        return (
          <Stack key={index} direction='row' alignItems='center' gap={1}>
            <Box
              sx={{
                width: 18,
                height: 18,
                bgcolor: bucketsColors[index],
              }}
            />

            <Typography variant='subtitleLabel' width={58}>
              {bucketCategories[index]}
            </Typography>

            <Typography variant='subtitleLabel'>
              (N = {bucketsCount[index]})
            </Typography>

            {bucketsCount[index] !== 0 && (
              <Typography variant='subtitleLabel'>
                {isDuplicate
                  ? `(${bucketValue})`
                  : isLastBucket
                    ? `(${bucketValue} - ${nextValue})`
                    : `(${bucketValue} - <${nextValue})`}
              </Typography>
            )}
          </Stack>
        );
      })}
    </Box>
  );
};

export default memo(MapLegend);
